import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { countdown, fmtTime, isDone, mapsHref } from "../../lib/liveUtils";
import { useAssistant } from "../assistant/AssistantProvider";
import { toast } from "sonner";
import { Plane, BedDouble, LogOut, Car, Ticket, Copy, MapPin, Phone, Radio, ChevronDown, ChevronRight, Sparkles, ArrowRight, Sunrise } from "lucide-react";
import { LiveStatusPill, LiveGateRow } from "../reservations/LiveStatusPill";

const KIND_ICONS = {
  flight: Plane, hotel_checkin: BedDouble, hotel_checkout: LogOut,
  car_pickup: Car, car_dropoff: Car, event: Ticket,
};

function localTodayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function copyConf(conf) {
  navigator.clipboard.writeText(conf).then(() => toast.success("Confirmation copied"));
}

function ItemRow({ it, now, highlight }) {
  const [openDetail, setOpenDetail] = useState(false);
  const Icon = KIND_ICONS[it.kind] || Ticket;
  const done = isDone(it, now);
  return (
    <div className={`border-b border-white/10 last:border-b-0 ${done ? "opacity-40" : ""}`} data-testid="cockpit-timeline-item">
      <button onClick={() => setOpenDetail((v) => !v)} className="w-full flex items-center gap-3 py-2.5 text-left">
        <Icon size={14} className={`shrink-0 ${highlight ? "text-ember" : "text-sand-400"}`} />
        <span className={`flex-1 min-w-0 truncate text-sm ${highlight ? "font-semibold text-white" : "text-sand-200"}`}>
          {it.title}{it.subtitle ? <span className="text-sand-500"> · {it.subtitle}</span> : null}
        </span>
        <span className="font-mono text-xs text-sand-400 shrink-0">
          {fmtTime(it.local)}{it.tz ? ` ${it.tz}` : ""}{it.time_estimated ? " est." : ""}
        </span>
        {openDetail ? <ChevronDown size={12} className="text-sand-500" /> : <ChevronRight size={12} className="text-sand-500" />}
      </button>
      {openDetail && (
        <div className="pb-3 pl-7 flex flex-wrap items-center gap-2" data-testid="cockpit-item-detail">
          {it.kind === "flight" && it.live ? (
            <div className="w-full flex flex-col gap-1 mb-1">
              <LiveStatusPill live={it.live} />
              <LiveGateRow live={it.live} tone="dark" />
            </div>
          ) : null}
          {(it.detail_rows || []).map(([k, v]) => (
            <span key={k} className="font-mono text-[10px] uppercase tracking-wide text-sand-400">{k}: <span className="text-sand-200">{v}</span></span>
          ))}
          {it.confirmation && (
            <button onClick={() => copyConf(it.confirmation)}
              className="flex items-center gap-1 border border-sand-600 px-2 py-1 font-mono text-[11px] text-sand-200 hover:border-white"
              data-testid="cockpit-item-conf">
              {it.confirmation} <Copy size={9} />
            </button>
          )}
          {it.maps_query && (
            <a href={mapsHref(it.maps_query)} target="_blank" rel="noreferrer"
              className="flex items-center gap-1 border border-sand-600 px-2 py-1 font-mono text-[11px] text-sand-200 hover:border-white">
              <MapPin size={9} /> Map
            </a>
          )}
          {it.phone && (
            <a href={`tel:${it.phone}`}
              className="flex items-center gap-1 border border-sand-600 px-2 py-1 font-mono text-[11px] text-sand-200 hover:border-white">
              <Phone size={9} /> Call
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function Banner({ current, next, now }) {
  const it = current || next;
  if (!it) return <p className="text-sm text-sand-400 mt-2">All items done — enjoy the rest of the trip.</p>;
  const Icon = KIND_ICONS[it.kind] || Ticket;
  const cd = current ? null : countdown(it.ts, it.local, now);
  return (
    <div className="mt-3 flex items-start gap-3">
      <Icon size={20} className="mt-0.5 text-ember shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-sand-400">
          {current ? "Now" : "Up next"}
        </p>
        <p className="font-medium text-lg truncate text-white" data-testid="cockpit-now-title">
          {it.title}{it.subtitle ? <span className="text-sand-400"> · {it.subtitle}</span> : null}
        </p>
        <p className="font-mono text-xs text-sand-400 mt-0.5">
          {fmtTime(it.local)}{it.tz ? ` ${it.tz}` : ""}
          {cd && cd !== "now" && <span className="ml-2 text-white font-semibold" data-testid="cockpit-countdown">{cd}</span>}
          {it.planned && <span className="ml-2 text-amber-400 uppercase text-[9px]">planned</span>}
        </p>
        {it.kind === "flight" && it.live ? (
          <div className="mt-2 flex flex-col gap-1">
            <LiveStatusPill live={it.live} />
            <LiveGateRow live={it.live} tone="dark" />
          </div>
        ) : null}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {it.confirmation && (
          <button onClick={() => copyConf(it.confirmation)}
            className="flex items-center gap-1.5 border border-sand-600 px-2.5 py-1.5 font-mono text-xs text-sand-200 hover:border-white"
            data-testid="cockpit-conf">
            {it.confirmation} <Copy size={10} className="text-sand-500" />
          </button>
        )}
        {it.maps_query && (
          <a href={mapsHref(it.maps_query)} target="_blank" rel="noreferrer"
            className="p-2 border border-sand-600 text-sand-200 hover:border-white" title="Open in Maps" data-testid="cockpit-maps">
            <MapPin size={14} />
          </a>
        )}
      </div>
    </div>
  );
}

export default function TodayCockpit({ home, variant = "full" }) {
  const assistant = useAssistant();
  const [now, setNow] = useState(new Date());
  const [showPast, setShowPast] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  if (!home?.active_trip) return null;
  const trip = home.active_trip;
  const items = home.items || [];
  const current = home.current_reservation;
  const next = home.next_reservation;
  const dxy = home.day_x_of_y;
  const projName = home.projected_location?.location?.name;

  const ask = (q) => assistant?.openAssistant?.({
    context: {
      screen: "live", trip_id: trip.id,
      current_reservation_id: current?.reservation_id,
      day_x_of_y: dxy || undefined,
      projected_location: projName || undefined,
    },
    prefill: q,
  });

  const flightNext = (current || next)?.kind === "flight";
  const chips = flightNext
    ? ["How tight is my connection?", "What's near my gate?", "Anything I should prep before this flight?"]
    : ["What's near my hotel?", "Ideas for tonight nearby?", "Anything I should prep for tomorrow?"];

  const todayIso = localTodayIso();
  const todays = items.filter((i) => (i.local || "").slice(0, 10) === todayIso);
  const past = todays.filter((i) => isDone(i, now));
  const upcoming = todays.filter((i) => !isDone(i, now));

  // ---- Imminent (pre-trip) variant ----
  if (variant === "imminent") {
    const it = next || items[0];
    const cd = it ? countdown(it.ts, it.local, now) : null;
    return (
      <div className="rounded-card border-2 border-ember bg-surface mb-4 px-5 sm:px-6 py-4" data-testid="today-cockpit-imminent">
        <p className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-ember">
          <span className="w-2 h-2 bg-ember rounded-full animate-pulse" />
          Starts soon · {trip.name}
        </p>
        {it && (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium text-base truncate" data-testid="cockpit-now-title">{it.title}{it.subtitle ? <span className="text-sand-500"> · {it.subtitle}</span> : null}</p>
              <p className="font-mono text-xs text-sand-500 mt-0.5">
                {fmtTime(it.local)}{it.tz ? ` ${it.tz}` : ""}
                {cd && cd !== "now" && <span className="ml-2 text-ink font-semibold" data-testid="cockpit-countdown">{cd}</span>}
              </p>
              {it.kind === "flight" && (
                <p className="text-xs text-sand-600 mt-1" data-testid="cockpit-checkin-hint">✓ Online check-in usually opens 24h before departure.</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {it.confirmation && (
                <button onClick={() => copyConf(it.confirmation)}
                  className="flex items-center gap-1.5 border border-sand-300 px-2.5 py-1.5 font-mono text-xs hover:border-ink" data-testid="cockpit-conf">
                  {it.confirmation} <Copy size={10} className="text-sand-400" />
                </button>
              )}
              <Link to={`/trips/${trip.id}`} className="btn-swiss btn-outline text-sm" data-testid="cockpit-view-trip">
                View trip <ArrowRight size={13} className="ml-2" />
              </Link>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---- Live (full / compact) variant — dark hero ----
  return (
    <div className="rounded-card bg-black text-white mb-4 overflow-hidden" data-testid="today-cockpit">
      <div className="px-5 sm:px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-ember">
            <span className="w-2 h-2 bg-ember rounded-full animate-pulse" />
            Live · {trip.name}{dxy ? ` · Day ${dxy.x} of ${dxy.y}` : ""}
          </p>
          <Link to={`/trips/${trip.id}/live`} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-sand-400 hover:text-white" data-testid="cockpit-open-live">
            <Radio size={11} /> Full itinerary
          </Link>
        </div>

        <Banner current={current} next={next} now={now} />

        {variant === "full" && (
          <>
            {(past.length > 0 || upcoming.length > 0) && (
              <div className="mt-4 border-t border-white/10 pt-1" data-testid="cockpit-timeline">
                {past.length > 0 && (
                  <button onClick={() => setShowPast((v) => !v)}
                    className="w-full text-left font-mono text-[10px] uppercase tracking-wide text-sand-500 py-2 hover:text-sand-300"
                    data-testid="cockpit-past-toggle">
                    ✓ {past.length} earlier item{past.length !== 1 ? "s" : ""} today {showPast ? "▾" : "▸"}
                  </button>
                )}
                {showPast && past.map((i) => <ItemRow key={i.id} it={i} now={now} />)}
                {upcoming.map((i) => (
                  <ItemRow key={i.id} it={i} now={now} highlight={current ? i.id === current.id : next ? i.id === next.id : false} />
                ))}
                {upcoming.length === 0 && past.length > 0 && (
                  <p className="text-xs text-sand-500 py-2">Nothing else scheduled today.</p>
                )}
              </div>
            )}

            {home.tomorrow && (now.getHours() >= 18 || upcoming.length === 0) && (
              <div className="mt-4 border-t border-white/10 pt-3" data-testid="cockpit-tomorrow">
                <p className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-sand-500">
                  <Sunrise size={11} className="text-hour" /> Tomorrow
                </p>
                {home.tomorrow.first_item ? (
                  <p className="text-sm text-sand-200 mt-1.5" data-testid="cockpit-tomorrow-first">
                    {home.tomorrow.first_item.title}
                    <span className="text-sand-500">
                      {" "}· {fmtTime(home.tomorrow.first_item.local)}
                      {home.tomorrow.first_item.tz ? ` ${home.tomorrow.first_item.tz}` : ""}
                      {home.tomorrow.first_item.time_estimated ? " est." : ""}
                    </span>
                  </p>
                ) : (
                  <p className="text-sm text-sand-400 mt-1.5" data-testid="cockpit-tomorrow-open">Nothing scheduled — open day.</p>
                )}
                {(home.tomorrow.wake_by || home.tomorrow.sunrise) && (
                  <p className="font-mono text-[10px] uppercase tracking-wide text-sand-500 mt-1" data-testid="cockpit-tomorrow-meta">
                    {[home.tomorrow.wake_by && `Wake by ~${home.tomorrow.wake_by}`,
                      home.tomorrow.sunrise && `Sunrise ${home.tomorrow.sunrise}`]
                      .filter(Boolean).join(" · ")}
                  </p>
                )}
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2" data-testid="cockpit-ask-chips">
              {chips.map((q) => (
                <button key={q} onClick={() => ask(q)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-sand-600 px-3 py-1.5 text-xs text-sand-200 hover:border-ember hover:text-white transition-colors"
                  data-testid="cockpit-ask-chip">
                  <Sparkles size={11} className="text-ember" /> {q}
                </button>
              ))}
            </div>
          </>
        )}

        <p className="mt-4 pt-3 border-t border-white/10 font-mono text-[10px] uppercase tracking-wide text-sand-500" data-testid="cockpit-context">
          {[trip.destination, projName && `You're in ${projName}`, dxy && `Day ${dxy.x}/${dxy.y}`,
            `Local ${now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`]
            .filter(Boolean).join(" · ")}
        </p>
      </div>
    </div>
  );
}
